/** Top navigation + page container — the viobean-style chrome. Nav items
 * are role-derived here in ONE place; pages never decide visibility. */
import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { completeTour } from "../../api/auth";
import { useAuth } from "../../auth/AuthContext";
import { useI18n } from "../../i18n";
import { NotificationsBell } from "../../features/notifications/NotificationsBell";
import { ProductTour } from "../tour/ProductTour";
import { buildTourSteps } from "../tour/tourSteps";

/** Every menu item the app can show. Exported so the guided tour is forced
 *  (by the compiler) to carry an explanation for each one. */
export type NavKey =
  | "nav.dashboard" | "nav.learn" | "nav.practice" | "nav.speak"
  | "nav.team" | "nav.access" | "nav.tenants" | "nav.billing"
  | "nav.revenue" | "nav.ai" | "nav.reference" | "nav.store" | "nav.orders"
  | "nav.classes" | "nav.aiUsage" | "nav.notebook";

export interface NavItem {
  to: string;
  key: NavKey;
}

/** School-admin surfaces: meaningless for a class of one. Hidden while the
 *  account is solo, and back the moment it has more than one seat. Billing
 *  is deliberately NOT here — it's the upgrade path, so hiding it behind an
 *  upgrade would be circular. */
const TEAM_MANAGEMENT: NavKey[] = ["nav.team", "nav.access", "nav.classes", "nav.aiUsage"];

export function navItemsFor(
  role: string | undefined,
  opts: { solo?: boolean } = {},
): NavItem[] {
  if (opts.solo) {
    return navItemsFor(role).filter((i) => !TEAM_MANAGEMENT.includes(i.key));
  }
  return baseNavItemsFor(role);
}

function baseNavItemsFor(role: string | undefined): NavItem[] {
  if (role === "super_admin") {
    return [
      { to: "/dashboard", key: "nav.dashboard" },
      { to: "/tenants", key: "nav.tenants" },
      { to: "/orders", key: "nav.orders" },
      { to: "/revenue", key: "nav.revenue" },
      { to: "/ai-settings", key: "nav.ai" },
      { to: "/ai-usage", key: "nav.aiUsage" },
    ];
  }
  if (role === "tenant_admin") {
    return [
      { to: "/dashboard", key: "nav.dashboard" },
      { to: "/learn", key: "nav.learn" },
      { to: "/reference", key: "nav.reference" },
      { to: "/notebook", key: "nav.notebook" },
      { to: "/store", key: "nav.store" },
      { to: "/practice", key: "nav.practice" },
      { to: "/speak", key: "nav.speak" },
      { to: "/team", key: "nav.team" },
      { to: "/classes", key: "nav.classes" },
      { to: "/access", key: "nav.access" },
      { to: "/billing", key: "nav.billing" },
      { to: "/ai-usage", key: "nav.aiUsage" },
    ];
  }
  // Every other tenant role — learners first among them — lives on the
  // learning surface. NO Store: in a school the admin buys, and a pupil
  // who can't order shouldn't be shown a shop they'd only bounce off
  // (the API already refuses them). A SOLO learner is a tenant_admin, so
  // they keep the Store via the branch above.
  return [
    { to: "/dashboard", key: "nav.dashboard" },
    { to: "/learn", key: "nav.learn" },
    { to: "/reference", key: "nav.reference" },
    { to: "/notebook", key: "nav.notebook" },
    { to: "/practice", key: "nav.practice" },
    { to: "/speak", key: "nav.speak" },
  ];
}

export function AppShell() {
  const { user, logout, refresh } = useAuth();
  const { t, locale, setLocale } = useI18n();
  const navigate = useNavigate();

  const items = navItemsFor(user?.role, { solo: user?.is_solo });

  // Guided tour: runs once for a tenant user who hasn't seen it, and is
  // replayable from the "?" button. `dismissed` keeps it closed for the rest
  // of the session even if persisting the flag fails.
  const [tourOpen, setTourOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!dismissed && user?.tenant_id && !user.tour_completed) setTourOpen(true);
  }, [user, dismissed]);

  const closeTour = () => {
    setTourOpen(false);
    setDismissed(true);
    // Remember it server-side so it doesn't re-run on their next device.
    completeTour()
      .then(refresh)
      .catch(() => undefined); // non-fatal: the tour just stays local
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
          <NavLink to="/dashboard" className="flex items-center gap-2 text-xl font-extrabold text-brand-dark">
            {user?.tenant_name ? (
              user.tenant_logo_url ? (
                <img src={user.tenant_logo_url} alt="" className="h-8 w-8 rounded-full object-cover shadow-sm" />
              ) : (
                <span aria-hidden className="bg-grad-hero grid h-8 w-8 place-items-center rounded-full text-sm font-bold text-white shadow-sm">
                  {user.tenant_name.trim().charAt(0).toUpperCase()}
                </span>
              )
            ) : (
              <span aria-hidden className="bg-grad-hero grid h-8 w-8 place-items-center rounded-full text-sm text-white shadow-sm">✓</span>
            )}
            <span>{user?.tenant_name ?? t("app.name").toLowerCase()}</span>
          </NavLink>
          <nav className="flex items-center gap-1">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-tour={item.key}
                className={({ isActive }) =>
                  `rounded-control px-4 py-2 text-sm font-semibold ${isActive ? "bg-brand-soft text-brand-dark" : "text-muted hover:text-ink"}`
                }
              >
                {t(item.key)}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            {user?.tenant_id && (
              <button
                onClick={() => { setDismissed(false); setTourOpen(true); }}
                aria-label={t("tour.help")}
                title={t("tour.help")}
                className="grid h-7 w-7 place-items-center rounded-full border border-line text-sm font-bold text-muted hover:border-brand hover:text-brand"
              >
                ?
              </button>
            )}
            <button
              aria-label="Switch language"
              onClick={() => setLocale(locale === "en" ? "de" : "en")}
              className="rounded-control border border-line px-2 py-1 text-xs font-bold uppercase text-muted hover:text-ink"
            >
              {locale}
            </button>
            <NotificationsBell />
            <NavLink
              to="/profile"
              aria-label={t("nav.profile")}
              title={t("nav.profile")}
              className="grid h-9 w-9 place-items-center rounded-full bg-brand-soft text-sm font-bold text-brand-dark hover:ring-2 hover:ring-brand/30"
            >
              {(user?.full_name || "?").charAt(0).toUpperCase()}
            </NavLink>
            <button onClick={handleLogout} className="text-sm font-semibold text-muted hover:text-danger">
              {t("nav.logout")}
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-8">
        <Outlet />
      </main>
      {tourOpen && (
        <ProductTour steps={buildTourSteps(t, items)} onClose={closeTour} />
      )}
    </div>
  );
}
